import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '../NotificationSystem';
import { atpSchedulerService } from '../../services/atpSchedulerService';
import { auditRecordService } from '../../services/auditRecordService';
import { capaService } from '../../services/capaService';
import { incidentService } from '../../services/incidentService';
import Modal from '../ui/Modal';
import './ATPSchedulerRunModal.css';

const SOURCE_KEYS = [
  { value: 'planned_ipc', labelKey: 'atpScheduler.sourcePlannedIpc', hasObject: false },
  { value: 'audit', labelKey: 'atpScheduler.sourceAudit', hasObject: true },
  { value: 'direct_observation', labelKey: 'atpScheduler.sourceDirectObservation', hasObject: false },
  { value: 'incident', labelKey: 'atpScheduler.sourceIncident', hasObject: true },
  { value: 'capa', labelKey: 'atpScheduler.sourceCapa', hasObject: true }
];

const ATPSchedulerRunModal = ({ organizationId, onClose, onSuccess }) => {
  const { t } = useTranslation('ipc');
  const { notify } = useNotifications();
  const [source, setSource] = useState('');
  const [sourceId, setSourceId] = useState('');
  const [sourceRef, setSourceRef] = useState('');
  const [sourceObjects, setSourceObjects] = useState([]);
  const [loadingObjects, setLoadingObjects] = useState(false);
  const [running, setRunning] = useState(false);
  const [errors, setErrors] = useState({});

  const selectedSourceDef = SOURCE_KEYS.find(s => s.value === source);

  useEffect(() => {
    if (!source || !selectedSourceDef?.hasObject) {
      setSourceObjects([]);
      setSourceId('');
      setSourceRef('');
      return;
    }

    setLoadingObjects(true);
    setSourceId('');
    setSourceRef('');

    const loadObjects = async () => {
      try {
        let objects = [];

        if (source === 'audit') {
          const audits = await auditRecordService.getByOrganization(organizationId);
          if (process.env.NODE_ENV === 'development') {
            const uniq = [...new Set(audits.map(a => a.status).filter(Boolean))];
            if (uniq.length) console.log('[ATPSchedulerRunModal] audit status values:', uniq);
          }
          objects = audits
            .filter(a =>
              a.status === 'in_progress' ||
              a.status === 'active' ||
              a.status === 'ongoing'
            )
            .map(a => ({
              id: a.id,
              label: a.title || a.name || a.scope || `Audit ${a.id}`,
              ref: a.auditCode || a.code || a.id
            }));
        }

        if (source === 'capa') {
          const [planned, inProgress] = await Promise.all([
            capaService.listByOrganization(organizationId, { status: 'planned' }),
            capaService.listByOrganization(organizationId, { status: 'in_progress' })
          ]);
          const byId = new Map();
          [...planned, ...inProgress].forEach(c => {
            if (c?.id) byId.set(c.id, c);
          });
          objects = [...byId.values()].map(c => ({
            id: c.id,
            label: (c.actionDescription && String(c.actionDescription).slice(0, 60)) || `CAPA ${c.id}`,
            ref: c.sourceRef || c.id
          }));
        }

        if (source === 'incident') {
          const incidents = await incidentService.getIncidentsByOrganization(organizationId);
          if (process.env.NODE_ENV === 'development') {
            const uniq = [...new Set(incidents.map(i => i.status).filter(Boolean))];
            if (uniq.length) console.log('[ATPSchedulerRunModal] incident status values:', uniq);
          }
          objects = (incidents || [])
            .filter(i => i.status !== 'closed' && i.status !== 'resolved')
            .map(i => ({
              id: i.id,
              label: i.title || (i.description && String(i.description).slice(0, 60)) || `Incident ${i.id}`,
              ref: i.incidentCode || i.code || i.id
            }));
        }

        setSourceObjects(objects);
      } catch (err) {
        console.warn('[ATPSchedulerRunModal] Could not load source objects:', err);
        setSourceObjects([]);
      } finally {
        setLoadingObjects(false);
      }
    };

    loadObjects();
  }, [source, organizationId, selectedSourceDef?.hasObject]);

  const handleSourceObjectChange = (objectId) => {
    const obj = sourceObjects.find(o => o.id === objectId);
    setSourceId(objectId);
    setSourceRef(obj?.ref || objectId);
    if (errors.sourceId) setErrors(prev => { const e = { ...prev }; delete e.sourceId; return e; });
  };

  const validate = () => {
    const e = {};
    if (!source) e.source = t('atpScheduler.errorSourceRequired');
    if (selectedSourceDef?.hasObject && !sourceId) e.sourceId = t('atpScheduler.errorObjectRequired');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleRun = async () => {
    if (!validate()) return;
    setRunning(true);
    try {
      const result = await atpSchedulerService.callManualRun(
        organizationId,
        source,
        selectedSourceDef?.hasObject ? sourceId : null,
        selectedSourceDef?.hasObject ? sourceRef : null
      );

      if (result.error) {
        notify.error(t('atpScheduler.errorRunWithDetail', { detail: result.error }));
      } else {
        onSuccess(result);
      }
    } catch (err) {
      const msg = err?.message || err?.code || t('atpScheduler.errorUnknown');
      notify.error(t('atpScheduler.errorRunWithDetail', { detail: msg }));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={t('atpScheduler.modalTitle')} size="medium">
      <div className="atp-run-modal">
        <p className="modal-desc">{t('atpScheduler.modalDescription')}</p>

        <div className="form-field">
          <label>{t('atpScheduler.sourceLabel')} <span className="required">*</span></label>
          <select
            value={source}
            onChange={e => {
              setSource(e.target.value);
              if (errors.source) setErrors(prev => { const er = { ...prev }; delete er.source; return er; });
            }}
          >
            <option value="">{t('atpScheduler.selectSource')}</option>
            {SOURCE_KEYS.map(s => (
              <option key={s.value} value={s.value}>{t(s.labelKey)}</option>
            ))}
          </select>
          {errors.source && <span className="error">{errors.source}</span>}
        </div>

        {selectedSourceDef?.hasObject && (
          <div className="form-field">
            <label>
              {source === 'audit' && t('atpScheduler.labelAuditObject')}
              {source === 'incident' && t('atpScheduler.labelIncidentObject')}
              {source === 'capa' && t('atpScheduler.labelCapaObject')}
              {' '}<span className="required">*</span>
            </label>
            {loadingObjects ? (
              <div className="loading-hint">{t('atpScheduler.loadingObjects')}</div>
            ) : sourceObjects.length === 0 ? (
              <div className="empty-hint">
                {source === 'audit' && t('atpScheduler.emptyAudits')}
                {source === 'incident' && t('atpScheduler.emptyIncidents')}
                {source === 'capa' && t('atpScheduler.emptyCapas')}
              </div>
            ) : (
              <select
                value={sourceId}
                onChange={e => handleSourceObjectChange(e.target.value)}
              >
                <option value="">{t('atpScheduler.selectObject')}</option>
                {sourceObjects.map(o => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
            )}
            {errors.sourceId && <span className="error">{errors.sourceId}</span>}
          </div>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose} disabled={running}>
            {t('atpScheduler.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleRun} disabled={running || !source}>
            {running ? t('atpScheduler.running') : t('atpScheduler.confirmRun')}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ATPSchedulerRunModal;
